import React, { useEffect, useState } from 'react';
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButton as IonicButton,
    IonIcon,
    IonSpinner
} from '@ionic/react';
import { people, search as searchIcon, refreshOutline, settingsOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Card,
    CardContent,
    Grid,
    Stack,
    InputAdornment,
    Button,
    alpha
} from '@mui/material';
import { getAppSettings } from '../../../common/utils';
import { Vendor } from '../../../models/vendor';
import * as VendorService from '../../../api/vendor.service';
import { DataTable } from '../../../components/DataTable';

const VendorList: React.FC = () => {
    const { siteName } = getAppSettings();
    const history = useHistory();
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [stats, setStats] = useState({ total: 0 });

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const response = await VendorService.getVendors();
            const data = response.data || [];
            setVendors(data);
            setStats({ total: data.length });
        } catch (error) {
            console.error("Failed to fetch vendors", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, []);

    const filteredVendors = vendors.filter(v =>
        v.vendor_name.toLowerCase().includes(searchText.toLowerCase()) ||
        v.email.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const columns = [
        {
            id: 'vendor_name',
            label: 'Vendor Name',
            minWidth: 250,
            format: (value: string) => (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                    {value}
                </Typography>
            )
        },
        {
            id: 'email',
            label: 'Email',
            minWidth: 200,
            format: (value: string) => (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {value}
                </Typography>
            )
        },
        {
            id: 'phone',
            label: 'Phone',
            minWidth: 150,
            format: (value: string) => (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {value}
                </Typography>
            )
        },
        {
            id: 'actions',
            label: 'Actions',
            minWidth: 150,
            align: 'center' as const,
            format: (_: any, row: Vendor) => (
                <Button
                    size="small"
                    variant="outlined"
                    sx={{
                        borderColor: '#10b981',
                        color: '#10b981',
                        '&:hover': {
                            borderColor: '#059669',
                            bgcolor: alpha('#10b981', 0.05)
                        }
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        history.push(`/folder/vendor/${row.vendor_id}`);
                    }}
                >
                    Configure
                </Button>
            )
        }
    ];

    return (
        <IonPage className='fullscreen pageDown'>
            <IonHeader>
                <IonToolbar style={{
                    '--background': 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '--color': '#ffffff'
                }}>
                    <IonTitle style={{ fontWeight: 600 }}>Vendor Management</IonTitle>
                    <IonicButton
                        slot="end"
                        onClick={fetchVendors}
                        style={{
                            '--background': '#ffffff',
                            '--color': '#10b981',
                            fontWeight: 600
                        }}
                    >
                        <IonIcon icon={refreshOutline} slot="start" />
                        Refresh
                    </IonicButton>
                </IonToolbar>
            </IonHeader>

            <IonContent style={{ '--background': '#f8fafc' }}>
                <Box sx={{ p: 3 }}>
                    {/* Stats Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                            }}>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <IonIcon icon={people} style={{ fontSize: '2.5rem' }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {stats.total}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                Total Vendors
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                            }}>
                                <CardContent>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <IonIcon icon={settingsOutline} style={{ fontSize: '2.5rem' }} />
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                                {filteredVendors.length}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                Configured Vendors
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Search Bar */}
                    <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <CardContent>
                            <TextField
                                fullWidth
                                placeholder="Search vendors by name or email..."
                                variant="outlined"
                                value={searchText}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IonIcon icon={searchIcon} style={{ fontSize: '1.5rem', color: '#64748b' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#f8fafc',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Data Table */}
                    <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                        <CardContent sx={{ p: 0 }}>
                            {loading ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <IonSpinner name="crescent" />
                                    <Typography color="text.secondary" sx={{ mt: 2 }}>Loading vendors...</Typography>
                                </Box>
                            ) : filteredVendors.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <IonIcon icon={people} style={{ fontSize: '4rem', color: '#cbd5e1', marginBottom: '1rem' }} />
                                    <Typography color="text.secondary">No vendors found.</Typography>
                                </Box>
                            ) : (
                                <DataTable
                                    columns={columns}
                                    rows={filteredVendors}
                                    onRowClick={(row) => history.push(`/accounting/vendors/${row.vendor_id}`)}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </IonContent>
        </IonPage>
    );
};

export default VendorList;
